import {
  PayrollSchedule,
  PayrollScheduleDayOfWeek,
  PayrollScheduleFrequency
} from "@wingspanhq/wingspan-api-sdk";
import {
  MAX_WEEK_INTERVAL,
  PayrollCadenceFormData,
  PayrollCadenceOption
} from "../components/FieldsPayrollCadence/types";
import { WeekDay } from "../types/time";
import { dayInIntervalToWeekDayMapping } from "../utils/time";
import { PayrollCadence } from "./selectorPayrollCadence";

const scheduleDayOfWeekToWeekDay: Record<PayrollScheduleDayOfWeek, WeekDay> = {
  [PayrollScheduleDayOfWeek.MONDAY]: WeekDay.Monday,
  [PayrollScheduleDayOfWeek.TUESDAY]: WeekDay.Tuesday,
  [PayrollScheduleDayOfWeek.WEDNESDAY]: WeekDay.Wednesday,
  [PayrollScheduleDayOfWeek.THURSDAY]: WeekDay.Thursday,
  [PayrollScheduleDayOfWeek.FRIDAY]: WeekDay.Friday,
  [PayrollScheduleDayOfWeek.SATURDAY]: WeekDay.Saturday,
  [PayrollScheduleDayOfWeek.SUNDAY]: WeekDay.Sunday
};

/**
 * `WeekDay` and the `dayInInterval` mappings run Monday-first (1–7), while
 * `Date.getUTCDay()` runs Sunday-first (0–6). Indexing one with the other reads
 * a Friday schedule as Saturday, so every crossing goes through here.
 */
const jsDayToDayInInterval = (jsDay: number) => (jsDay === 0 ? 7 : jsDay);

/**
 * The cadence form fields a payer's contractor `PayrollSchedule` answers to.
 *
 * The weekday a weekly schedule lands on is `dayOfWeek`. A schedule written
 * before that field existed carries it only in `startDate`, and a schedule the
 * cadence resource will not serve carries it only in the pay dates it produced —
 * so the weekday is read from whichever of the three answers first.
 *
 * `derivedCadence` is the fallback for when the cadence resource is unavailable
 * — see {@link selectorPayrollCadence}. The schedule wins when both are present,
 * since it is the stored intent rather than a reading of its output.
 */
export const selectorPayrollCadenceFormData = ({
  schedule,
  derivedCadence
}: {
  schedule?: PayrollSchedule;
  derivedCadence?: PayrollCadence;
}): PayrollCadenceFormData => {
  const frequency = schedule?.frequency ?? derivedCadence?.frequency;
  const every = schedule?.frequency
    ? (schedule.interval ?? 1)
    : (derivedCadence?.interval ?? 1);

  const anchorDay =
    schedule?.startDate !== undefined
      ? new Date(schedule.startDate).getUTCDay()
      : derivedCadence?.weekday;

  const dayInInterval = schedule?.dayOfWeek
    ? scheduleDayOfWeekToWeekDay[schedule.dayOfWeek]
    : anchorDay === undefined
      ? WeekDay.Monday
      : dayInIntervalToWeekDayMapping[jsDayToDayInInterval(anchorDay)];

  switch (frequency) {
    case PayrollScheduleFrequency.DAILY:
      return {
        interval: PayrollCadenceOption.Everyday,
        every: 1,
        dayInInterval
      };
    case PayrollScheduleFrequency.SEMI_MONTHLY:
      return {
        interval: PayrollCadenceOption.SemiMonthly,
        every: 1,
        dayInInterval
      };
    case PayrollScheduleFrequency.WEEKLY:
      return {
        interval: PayrollCadenceOption.Week,
        every: Math.min(every, MAX_WEEK_INTERVAL),
        dayInInterval
      };
    default:
      return {
        interval: PayrollCadenceOption.Monthly,
        every: 1,
        dayInInterval
      };
  }
};
